import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconName } from 'shared';
import classNames from 'classnames';

import { media } from 'context';
import { useListFocus } from 'hooks';
import useFieldSize from 'hooks/useFieldSize';
import { HEADER_Z_INDEX } from './constants';
import useUniqueId from './helpers/useUniqueId';
import TextInput, { Props as TextInputProps } from './inputs/TextInput';
import { FieldSize } from './types';
import { highlightMatches } from './utils';

export interface Props<T extends string | number> extends Pick<
  TextInputProps,
  | 'onChange'
  | 'label'
  | 'size'
  | 'disabled'
  | 'onClear'
  | 'placeholder'
  | 'onFocus'
  | 'onBlur'
  | 'isReadOnly'
  | 'autoFocus'
> {
  results: Result<T>[];
  query?: string;
  onSelect: (result: Result<T>) => void;
  showResultsWhenEmpty?: boolean;
  maxItems?: number;
  highlightMatch?: boolean;
  required?: boolean;
  allowNewEntry?: boolean;
  hideLabel?: boolean;
  icon?: IconName;
  error?: string;
  /** When false, the results list stays open after a pick (e.g. multi-select tag filters). Default true. */
  closeResultsOnSelect?: boolean;
  /** When set, portaled results re-anchor after this value changes (e.g. selected count above the field). */
  repositionKey?: string | number;
}

type ResultProps<T extends string | number> = Pick<Props<T>, 'results' | 'onSelect' | 'highlightMatch'>;

export interface Result<T extends string | number> {
  value: T;
  label: string;
}

const CLASSNAME = 'Typeahead';

const Typeahead = <T extends string | number>({
  label,
  hideLabel = false,
  query,
  onChange,
  onSelect,
  showResultsWhenEmpty = false,
  allowNewEntry = false,
  maxItems = 8,
  highlightMatch,
  disabled,
  onClear,
  placeholder,
  icon,
  required = false,
  error,
  onFocus,
  onBlur,
  isReadOnly,
  autoFocus,
  closeResultsOnSelect = true,
  repositionKey,
  ...props
}: Props<T>) => {
  const { isTouchScreen } = useContext(media);
  const scaledSize = useFieldSize();
  const [resultsVisible, setResultsVisible] = useState<boolean>(false);
  const [resultsPosition, setResultsPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const resultsPortalRef = useRef<HTMLDivElement>(null);
  const size = props.size || scaledSize;
  const hasQuery = query && query.length > 0;
  let results = hasQuery ? props.results : showResultsWhenEmpty ? props.results : [];
  const queryDoesNotMatch = query && !results.map(({ label }) => label.toLowerCase()).includes(query.toLowerCase());

  if (allowNewEntry && query && queryDoesNotMatch) {
    results = [{ label: `Add "${query}"`, value: query as T }, ...results];
  }
  const slicedResults = results.slice(0, maxItems);
  const [suppressValidation, setSuppressValidation] = useState<boolean>();
  const id = useUniqueId();

  const [focusedIndex, setFocusedIndex] = useListFocus({
    divId: id,
    size: slicedResults.length,
    handleEnter: index => {
      const result = slicedResults[index];

      if (result) onSelect(result);

      if (closeResultsOnSelect) {
        setResultsVisible(false);
      }
    }
  });

  useEffect(() => {
    setFocusedIndex(slicedResults.length > 0 ? 0 : -1);
  }, [query, setFocusedIndex, slicedResults.length]);

  useLayoutEffect(() => {
    if (!autoFocus) {
      return;
    }
    ref.current?.querySelector<HTMLInputElement>('input')?.focus();
    setResultsVisible(true);
  }, [autoFocus]);

  const updateResultsPosition = () => {
    if (!ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    setResultsPosition({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width
    });
  };

  useLayoutEffect(() => {
    if (!resultsVisible) {
      setResultsPosition(null);
      return;
    }

    updateResultsPosition();

    const root = ref.current;
    if (!root) {
      return;
    }

    // Reposition when this field moves (e.g. filter popover grows as selected tags wrap).
    const resizeObserver = new ResizeObserver(() => {
      updateResultsPosition();
    });

    let node: HTMLElement | null = root;
    let depth = 0;
    while (node && depth < 12) {
      resizeObserver.observe(node);
      node = node.parentElement;
      depth += 1;
    }

    const handleReposition = () => updateResultsPosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [resultsVisible, slicedResults.length, query, size, repositionKey]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || resultsPortalRef.current?.contains(target)) {
        return;
      }
      setResultsVisible(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const resultList = (
    <ResultList
      results={slicedResults}
      onSelect={result => {
        setSuppressValidation(false);
        onSelect(result);
        if (closeResultsOnSelect) {
          setResultsVisible(false);
        }
      }}
      onMouseDown={() => {
        setSuppressValidation(true);
      }}
      size={size}
      highlightMatch={highlightMatch}
      query={query}
      focusedIndex={focusedIndex}
      isVisible={resultsVisible}
      isPortaled
      position={resultsPosition ?? undefined}
      portalRef={resultsPortalRef}
    />
  );

  return (
    <div
      id={id}
      className={classNames(CLASSNAME, `is-size-${size}`)}
      ref={ref}
      onBlur={() => {
        if (!isTouchScreen) return;

        setTimeout(() => {
          setResultsVisible(false);
        }, 35);
      }}
    >
      <TextInput
        icon={icon}
        label={label}
        hideLabel={hideLabel}
        value={query}
        onChange={params => {
          onChange(params);
          if (!resultsVisible) {
            setResultsVisible(true);
          }
        }}
        isReadOnly={isReadOnly}
        size={size}
        onFocus={params => {
          setResultsVisible(true);
          onFocus && onFocus(params);
        }}
        onBlur={params => {
          onBlur && onBlur(params);
        }}
        disabled={disabled}
        onClear={onClear}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required={required}
        suppressValidation={suppressValidation}
        onKeyDown={({ key }) => {
          if (key === 'Tab') {
            setResultsVisible(false);
          }
        }}
        // This is causing a bug ... why was it necessary?
        // onBlur={() => {
        //   if (allowNewEntry && query && queryDoesNotMatch) {
        //     onSelect({ label: query, value: query as T });
        //   }
        // }}
        error={error}
      />
      {resultsVisible && resultsPosition && typeof document !== 'undefined'
        ? ReactDOM.createPortal(resultList, document.body)
        : null}
    </div>
  );
};

interface ResultListProps<T extends string | number> extends ResultProps<T> {
  size: FieldSize;
  query?: string;
  onMouseDown: VoidFunction;
  focusedIndex: number;
  isVisible?: boolean;
  isPortaled?: boolean;
  position?: { top: number; left: number; width: number };
  portalRef?: React.RefObject<HTMLDivElement>;
}

const ResultList = <T extends string | number>({
  results,
  size,
  onSelect,
  onMouseDown,
  highlightMatch,
  query,
  focusedIndex,
  isVisible,
  isPortaled,
  position,
  portalRef
}: ResultListProps<T>) => {
  return (
    <div
      ref={portalRef}
      className={classNames(CLASSNAME, `is-size-${size}`, 'Typeahead-results', {
        'is-visible': isVisible,
        'is-portaled': isPortaled
      })}
      style={
        isPortaled && position
          ? {
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: HEADER_Z_INDEX + 2
            }
          : undefined
      }
    >
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        {results.map(({ label, value }, index) => (
          <ResultItem
            key={`${String(value)}-${index}`}
            label={label}
            focused={index === focusedIndex}
            value={value}
            query={query}
            onSelect={onSelect}
            onMouseDown={onMouseDown}
            results={results}
            size={size}
            highlightMatch={highlightMatch}
          />
        ))}
      </ul>
    </div>
  );
};

interface ResultItemProps<T extends string | number> extends ResultProps<T>, Pick<Props<T>, 'size'>, Result<T> {
  size: FieldSize;
  focused: boolean;
  query?: string;
  onMouseDown: VoidFunction;
}

const ResultItem = <T extends string | number>({
  value,
  focused,
  onSelect,
  onMouseDown,
  highlightMatch,
  query,
  label
}: ResultItemProps<T>) => {
  const displayedLabel = highlightMatch
    ? highlightMatches({
        label,
        query,
        MatchedWrapper: ({ children }) => <span className="Typeahead-matchedText">{children}</span>
      })
    : label;

  return (
    <li
      className={classNames('Typeahead-resultItem', { 'is-focused': focused })}
      onMouseDown={e => {
        e.preventDefault();
        onMouseDown();
      }}
      onClick={() => {
        onSelect({ label, value });
      }}
    >
      <span className="Typeahead-resultText">{displayedLabel}</span>
    </li>
  );
};

export default Typeahead;
